import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  ReadingListDetail,
  ReadingListItem,
  ReadingListItemStatus,
} from '../../../dtos/reading-list';
import { httpErrorToMessage } from '../../../project-corpus/adapters/http-error-message';
import { ReadingListService } from '../../../services/reading-list.service';
import { ToastService } from '../../../services/toast.service';
import { BaseModalComponent } from '../../modals/base-modal/base-modal.component';
import { ConfirmModalComponent } from '../../modals/confirm-modal/confirm-modal.component';

@Component({
  selector: 'app-group-reading-list-detail',
  standalone: true,
  imports: [CommonModule, BaseModalComponent, ConfirmModalComponent],
  templateUrl: './group-reading-list-detail.component.html',
})
export class GroupReadingListDetailComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private readingListService = inject(ReadingListService);
  private sanitizer = inject(DomSanitizer);
  private toastService = inject(ToastService);

  readonly detail = signal<ReadingListDetail | null>(null);
  readonly loading = signal(true);
  readonly updatingItemId = signal<number | null>(null);
  readonly deleting = signal(false);
  readonly showDeleteConfirm = signal(false);
  readonly selectedPaper = signal<ReadingListItem | null>(null);
  readonly paperViewerUrl = signal<SafeResourceUrl | null>(null);
  readonly paperLoading = signal(false);
  readonly paperError = signal('');
  readonly errorMessage = signal('');
  readonly showAllReaders = signal(false);

  groupId = '';
  readingListId = '';
  private sub?: Subscription;
  private paperSub?: Subscription;
  private paperBlobUrl: string | null = null;

  ngOnInit(): void {
    this.sub = this.route.params.subscribe((params) => {
      this.groupId = this.route.parent?.snapshot.params['id'];
      this.readingListId = params['readingListId'];
      this.loadReadingList();
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.closePaper();
  }

  updateStatus(item: ReadingListItem, status: ReadingListItemStatus): void {
    if (!this.detail()?.summary.assignedToCurrentUser || item.status === status) {
      return;
    }

    this.updatingItemId.set(item.id);
    this.errorMessage.set('');
    this.readingListService
      .updateItemStatus(this.groupId, this.readingListId, item.id, status)
      .subscribe({
        next: (detail) => {
          this.detail.set(detail);
          this.updatingItemId.set(null);
        },
        error: (err) => {
          this.errorMessage.set(httpErrorToMessage(err));
          this.updatingItemId.set(null);
        },
      });
  }

  openPaper(item: ReadingListItem): void {
    this.selectedPaper.set(item);
    this.paperError.set('');
    this.paperViewerUrl.set(null);
    this.paperLoading.set(false);
    this.revokePaperBlobUrl();
    this.paperSub?.unsubscribe();

    if (item.pdfId) {
      this.paperLoading.set(true);
      this.paperSub = this.readingListService.getPaperContent(this.groupId, item.pdfId).subscribe({
        next: (blob) => {
          this.paperBlobUrl = URL.createObjectURL(blob);
          this.paperViewerUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(this.paperBlobUrl));
          this.paperLoading.set(false);
        },
        error: () => {
          this.paperError.set('Failed to load the PDF. Please try again.');
          this.paperLoading.set(false);
        },
      });
      return;
    }

    if (item.pdfUrl) {
      this.paperViewerUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(item.pdfUrl));
    } else {
      this.paperError.set('No readable PDF is attached to this paper.');
    }
  }

  closePaper(): void {
    this.selectedPaper.set(null);
    this.paperViewerUrl.set(null);
    this.paperError.set('');
    this.paperLoading.set(false);
    this.paperSub?.unsubscribe();
    this.revokePaperBlobUrl();
  }

  statusButtonClass(item: ReadingListItem, status: ReadingListItemStatus): Record<string, boolean> {
    const active = item.status === status;
    return {
      'border-amber-500 bg-amber-500 text-white': status === 'TO_READ' && active,
      'border-blue-600 bg-blue-600 text-white': status === 'IN_PROGRESS' && active,
      'border-green-600 bg-green-600 text-white': status === 'READ' && active,
      'border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100':
        status === 'TO_READ' && !active,
      'border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100':
        status === 'IN_PROGRESS' && !active,
      'border-green-200 bg-green-50 text-green-800 hover:bg-green-100':
        status === 'READ' && !active,
      'cursor-not-allowed opacity-60': this.updatingItemId() === item.id,
    };
  }

  askDelete(): void {
    this.showDeleteConfirm.set(true);
  }

  cancelDelete(): void {
    this.showDeleteConfirm.set(false);
  }

  deleteReadingList(): void {
    this.deleting.set(true);
    const title = this.detail()?.summary.title ?? 'reading list';
    this.readingListService.deleteReadingList(this.groupId, this.readingListId).subscribe({
      next: () => {
        this.deleting.set(false);
        this.toastService.success('Deleted', `"${title}" has been deleted.`);
        this.router.navigate(['/groups', this.groupId, 'reading-lists']);
      },
      error: (err) => {
        this.errorMessage.set(httpErrorToMessage(err));
        this.deleting.set(false);
        this.showDeleteConfirm.set(false);
      },
    });
  }

  goBack(): void {
    this.router.navigate(['/groups', this.groupId, 'reading-lists']);
  }

  statusLabel(status: ReadingListItemStatus): string {
    switch (status) {
      case 'IN_PROGRESS':
        return 'In Progress';
      case 'READ':
        return 'Read';
      default:
        return 'To Read';
    }
  }

  readerProgressLabel(readItems: number, totalItems: number): string {
    return `${readItems} / ${totalItems} read`;
  }

  private loadReadingList(): void {
    if (!this.groupId || !this.readingListId) {
      return;
    }

    this.loading.set(true);
    this.errorMessage.set('');
    this.readingListService.getReadingList(this.groupId, this.readingListId).subscribe({
      next: (detail) => {
        this.detail.set(detail);
        this.loading.set(false);
      },
      error: (err) => {
        this.errorMessage.set(httpErrorToMessage(err));
        this.loading.set(false);
      },
    });
  }

  private revokePaperBlobUrl(): void {
    if (this.paperBlobUrl) {
      URL.revokeObjectURL(this.paperBlobUrl);
      this.paperBlobUrl = null;
    }
  }
}
